/**
 * useReorderPlaylist Hook
 * Handles intelligent playlist reordering using selected strategy
 */

import { useMutation } from '@tanstack/react-query';
import { playlistService } from '../api/services/playlists';
import { useWorkbenchStore } from '../stores/workbench';
import { toast } from '../components/ui/Toast';
import type { Track, ReorderStrategyType } from '../api/types';

interface ReorderPlaylistParams {
  playlistId: string;
  strategy?: ReorderStrategyType;
  applyToSpotify?: boolean;
  startTrackId?: string | null;
}

/**
 * Hook to reorder playlist using selected strategy
 * Returns a mutation that calls the reorder endpoint
 */
export function useReorderPlaylist() {
  const setTracks = useWorkbenchStore((state) => state.setTracks);

  return useMutation({
    mutationFn: async ({ playlistId, strategy = 'full_dj_mode', applyToSpotify = false, startTrackId }: ReorderPlaylistParams) => {
      const response = await playlistService.reorder(playlistId, {
        strategy,
        applyToSpotify,
        startTrackId,
      });

      return response;
    },
    onSuccess: (result, variables) => {
      // Update workbench with reordered tracks
      // Convert URIs back to Track objects from current workbench state
      const store = useWorkbenchStore.getState();
      const currentTracks = store.tracks;

      // Create a map of URI -> Track for fast lookup
      const trackMap = new Map<string, Track>();
      currentTracks.forEach((track) => {
        trackMap.set(track.uri, track);
      });

      // Reorder tracks based on returned URIs
      const reorderedTracks: Track[] = [];
      for (const uri of result.track_uris) {
        const track = trackMap.get(uri);
        if (track) {
          reorderedTracks.push(track);
        }
      }

      // Update workbench state
      setTracks(reorderedTracks);

      // Show success message with quality score and strategy
      const qualityEmoji = result.quality_score >= 70 ? '🎉' : result.quality_score >= 50 ? '✨' : '👍';
      const appliedText = variables.applyToSpotify ? ' and applied to Spotify' : '';
      const strategyName = result.strategy_used.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

      toast.success(
        `${strategyName}${appliedText}! ${qualityEmoji} Quality: ${result.quality_score.toFixed(0)}/100 - ${result.reordered_count} tracks optimized`
      );

      if (result.missing_features_count > 0) {
        // Warning toast instead of error - tracks are now integrated smartly (not just dumped at end)
        toast.warning(
          `${result.missing_features_count} tracks without audio features were integrated into the sequence`
        );
      }
    },
    onError: (error: Error) => {
      toast.error(`Failed to reorder playlist: ${error.message}`);
    },
  });
}

/**
 * Check if playlist can be reordered
 * Returns true if there are at least 2 tracks
 */
export function useCanReorder(): boolean {
  const tracks = useWorkbenchStore((state) => state.tracks);
  return tracks.length >= 2;
}
